// components/Survey/QuestionsList.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QuestionForm from './QuestionForm';
import { questionService } from '../../services/questionService';
import { surveysAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../common/Modal';
import QuestionDetailsModal from './QuestionDetailsModal';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon, 
  EllipsisVerticalIcon, 
  QuestionMarkCircleIcon, 
  PlusIcon, 
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckCircleIcon // ADD THIS IMPORT
} from '@heroicons/react/24/outline';

const QuestionsList = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin, isCompanyAdmin, isAuthenticated } = useAuth();
  const canManage = isSuperAdmin || isCompanyAdmin;
  
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const dropdownRefs = useRef({});
  const surveyDropdownRef = useRef(null);

  // Load surveys on component mount
  useEffect(() => {
    if (isAuthenticated) {
      loadSurveys();
    }
  }, [isAuthenticated]);

  // Load questions when surveyId changes
  useEffect(() => { 
    if (isAuthenticated && surveyId) {
      console.log('🔄 Loading questions for Survey:', surveyId);
      loadSurveyQuestions();
      
      // Also update selectedSurvey when surveyId changes
      if (surveys.length > 0) {
        const currentSurvey = surveys.find(s => s.id === parseInt(surveyId));
        if (currentSurvey) {
          setSelectedSurvey(currentSurvey);
        }
      }
    }
  }, [isAuthenticated, surveyId, surveys]);

  const loadSurveys = async () => {
    try {
      console.log('📡 Loading surveys...');
      const response = await surveysAPI.getAll();
      console.log('✅ Surveys loaded:', response.data.length);
      setSurveys(response.data);
      
      // Set selected survey based on current surveyId
      const currentSurvey = response.data.find(s => s.id === parseInt(surveyId));
      if (currentSurvey) {
        setSelectedSurvey(currentSurvey);
      }
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Failed to load surveys');
    }
  };

  const loadSurveyQuestions = async (id) => {
    const targetSurveyId = id || surveyId;

    if (!targetSurveyId) {
      console.warn('⚠️ No survey ID available for question fetch.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setQuestions([]);
      setCategories([]);

      console.log('📡 Loading survey questions for survey:', targetSurveyId);
      
      const response = await questionService.getQuestionsBySurvey(targetSurveyId);
      console.log('✅ Survey questions response:', response);
      
      let categoriesData = [];
      let allQuestions = [];

      if (response) {
        if (response.success && response.data) {
          categoriesData = response.data;
        } else if (Array.isArray(response)) {
          categoriesData = response;
        } else if (response.categories && Array.isArray(response.categories)) {
          categoriesData = response.categories;
        } else if (response.data && Array.isArray(response.data)) {
          categoriesData = response.data;
        }
      }

      setCategories(categoriesData);

      allQuestions = categoriesData.flatMap(category => {
        const questions = category.questions || category.items || [];
        return questions.map(question => ({
          ...question,
          categoryName: category.name || category.title || 'Uncategorized',
          categoryId: category.id,
          categoryOrder: category.order || 0
        }));
      });

      console.log('✅ All questions loaded:', allQuestions.length);
      setQuestions(allQuestions);
    } catch (err) {
      console.error('❌ Error loading survey questions:', err);
      setError('Failed to load questions: ' + (err.message || 'Unknown error'));
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyChange = async (survey) => {
    console.log('🎯 Survey selected:', survey.name);
    setSelectedSurvey(survey);
    setSurveyDropdownOpen(false);

    // Reset UI states
    setQuestions([]);
    setCategories([]);
    setCurrentPage(1);
    setSearchTerm('');
    setFilterType('all');

    // Directly fetch questions for the selected survey
    await loadSurveyQuestions(survey.id); 
  };

  const handleBackToSurveys = () => {
    navigate('/surveys');
  };

  const handleDelete = async (id) => {
    if(!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await questionService.deleteSurveyQuestion(id);
      await loadSurveyQuestions();
      setActionDropdown(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete question: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEdit = (question) => { 
    setEditingQuestion(question); 
    setShowForm(true);
    setActionDropdown(null);
  };

  const handleView = (question) => {
    setViewingQuestion(question);
    setShowDetailsModal(true);
    setActionDropdown(null);
  };

  const handleActionClick = (questionId, e) => {
    e.stopPropagation();
    setActionDropdown(actionDropdown === questionId ? null : questionId);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideQuestionDropdown = Object.values(dropdownRefs.current).some(ref => 
        ref && ref.contains && ref.contains(event.target)
      );

      const clickedInsideSurveyDropdown = surveyDropdownRef.current && 
        surveyDropdownRef.current.contains(event.target);

      if (!clickedInsideQuestionDropdown) {
        setActionDropdown(null);
      }

      if (!clickedInsideSurveyDropdown) {
        setSurveyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search and filter logic
  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    if (searchTerm) {
      filtered = filtered.filter(question =>
        question.questionText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.helpText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.placeholder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.categoryName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType === 'required') {
      filtered = filtered.filter(question => question.isRequired);
    } else if (filterType === 'optional') {
      filtered = filtered.filter(question => !question.isRequired);
    }

    return filtered;
  }, [questions, searchTerm, filterType]);

  // Pagination logic
  const totalItems = filteredQuestions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQuestions = filteredQuestions.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilterType('all');
    setCurrentPage(1);
  };

  const getQuestionTypeBadge = (type) => {
    const typeConfigs = {
      1: { label: 'Rating', color: 'bg-purple-100 text-purple-800' },
      2: { label: 'Text', color: 'bg-blue-100 text-blue-800' },
      3: { label: 'Date', color: 'bg-green-100 text-green-800' },
      4: { label: 'Text Area', color: 'bg-yellow-100 text-yellow-800' },
      5: { label: 'Radio', color: 'bg-red-100 text-red-800' },
      6: { label: 'Hidden', color: 'bg-gray-100 text-gray-800' },
      7: { label: 'Number', color: 'bg-indigo-100 text-indigo-800' },
      8: { label: 'Email', color: 'bg-pink-100 text-pink-800' },
      9: { label: 'Checkbox', color: 'bg-orange-100 text-orange-800' },
      10: { label: 'Select', color: 'bg-teal-100 text-teal-800' },
      11: { label: 'File', color: 'bg-cyan-100 text-cyan-800' },
      12: { label: 'NPS', color: 'bg-lime-100 text-lime-800' },
      13: { label: 'Signature', color: 'bg-amber-100 text-amber-800' },
      14: { label: 'Paragraph', color: 'bg-emerald-100 text-emerald-800' }
    };

    const config = typeConfigs[type] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getRequiredBadge = (isRequired) => (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
      isRequired ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
    }`}>
      {isRequired ? 'Required' : 'Optional'}
    </span>
  );

  const getCategoryBadge = (categoryName) => (
    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
      {categoryName}
    </span>
  );

  if (!isAuthenticated) {
    return (
      <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Please log in to view questions</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header with Survey Selector - ALWAYS VISIBLE */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToSurveys}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Surveys"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {showForm 
                  ? (editingQuestion ? 'Edit Question' : 'Create New Question')
                  : 'Survey Questions Manager'
                }
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-gray-600">Survey:</span>
                <div className="relative" ref={surveyDropdownRef}>
                  <button
                    onClick={() => !showForm && setSurveyDropdownOpen(!surveyDropdownOpen)}
                    className={`flex items-center gap-2 px-3 py-1 rounded-lg border transition-colors ${
                      showForm
                        ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                        : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                    }`}
                  >
                    <span className="font-medium">
                      {selectedSurvey ? selectedSurvey.name : 'Select Survey'}
                    </span>
                    {!showForm && (
                      <ChevronDownIcon className={`w-4 h-4 transition-transform ${surveyDropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  
                  {surveyDropdownOpen && !showForm && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-64 max-h-60 overflow-y-auto">
                      {surveys.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No surveys available</div>
                      ) : (
                        surveys.map((survey) => (
                          <button
                            key={survey.id}
                            onClick={() => handleSurveyChange(survey)}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                              selectedSurvey?.id === survey.id 
                                ? 'bg-blue-50 text-blue-700 font-medium' 
                                : 'text-gray-700'
                            }`}
                          >
                            <div className="font-medium">{survey.name}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {survey.description || 'No description'}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {canManage && selectedSurvey && (
            <div className="flex gap-3">
              {showForm ? (
                <>
                  <button 
                    onClick={() => {
                      setShowForm(false);
                      setEditingQuestion(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => { 
                    setEditingQuestion(null); 
                    setShowForm(true); 
                  }}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium shadow-sm flex items-center gap-2 transition-colors"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create Question
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* SHOW EITHER QUESTION LIST OR QUESTION FORM */}
      {showForm ? (
        /* Question Form - HIDES the question list */
        <div data-question-form>
          <QuestionForm
            surveyId={parseInt(selectedSurvey?.id || surveyId)}
            initialData={editingQuestion}
            isEditing={!!editingQuestion}
            onSubmit={async (data) => {
              try {
                if (editingQuestion) {
                  await questionService.updateSurveyQuestion(editingQuestion.id, data);
                } else {
                  await questionService.createSurveyQuestion(data);
                }
                setShowForm(false);
                setEditingQuestion(null);
                await loadSurveyQuestions();
              } catch (err) {
                console.error('Error saving question:', err);
                setError('Failed to save question: ' + (err.message || 'Unknown error'));
              }
            }}
            onCancel={() => {
              setShowForm(false);
              setEditingQuestion(null);
            }}
          />
        </div>
      ) : (
        /* Question List - ONLY SHOWN when form is not visible */
        <>
          {/* Search and Filter Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input 
                      type="text" 
                      placeholder="Search questions by text, help text, category, or placeholder..." 
                      value={searchTerm} 
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                    {searchTerm && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Filter Dropdown */}
                  <div className="relative">
                    <FunnelIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-48 appearance-none bg-white transition-colors"
                    >
                      <option value="all">All Questions</option>
                      <option value="required">Required Only</option>
                      <option value="optional">Optional Only</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center flex-wrap gap-2 text-sm text-gray-600">
                <span className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full font-medium whitespace-nowrap">
                  Total: {questions.length}
                </span>
                <span className="bg-red-100 text-red-800 px-3 py-2 rounded-full font-medium whitespace-nowrap">
                  Required: {questions.filter(q => q.isRequired).length}
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-2 rounded-full font-medium whitespace-nowrap">
                  Optional: {questions.filter(q => !q.isRequired).length}
                </span>
                <span className="bg-purple-100 text-purple-800 px-3 py-2 rounded-full font-medium whitespace-nowrap">
                  Categories: {categories.length}
                </span>
              </div>
            </div>
          </div>

          {/* Questions List */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
            {/* Header Stats */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  All Questions for: {selectedSurvey?.name || 'Select a survey'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredQuestions.length} of {questions.length} questions across {categories.length} categories
                  {searchTerm && ` matching "${searchTerm}"`}
                  {filterType !== 'all' && ` (${filterType})`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">Loading questions...</span>
              </div>
            ) : filteredQuestions.length === 0 ? (
              /* No Data State */
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                  <QuestionMarkCircleIcon className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterType !== 'all' 
                    ? 'Try adjusting your search terms or filters' 
                    : selectedSurvey 
                      ? 'No questions found for this survey'
                      : 'Select a survey to view questions'
                  }
                </p>
                {!searchTerm && filterType === 'all' && canManage && selectedSurvey && (
                  <button 
                    onClick={() => { setEditingQuestion(null); setShowForm(true); }}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
                  >
                    Create Question
                  </button>
                )}
                {(searchTerm || filterType !== 'all') && (
                  <button
                    onClick={clearSearch}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              /* Questions List */
              <div className="space-y-4">
                {currentQuestions.map((question) => (
                  <div 
                    key={question.id}
                    className="border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200 bg-white"
                  >
                    {/* Question Header */}
                    <div className="p-4 flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <QuestionMarkCircleIcon className="w-5 h-5 text-blue-500" />
                          <div>
                            <h3 className="font-semibold text-gray-900">{question.questionText}</h3>
                            <p className="text-sm text-gray-500">
                              {question.helpText || 'No help text provided'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            {getQuestionTypeBadge(question.type)}
                          </span>
                          <span className="flex items-center gap-1">
                            {getRequiredBadge(question.isRequired)}
                          </span>
                          <span className="flex items-center gap-1">
                            {getCategoryBadge(question.categoryName)}
                          </span>
                          <span>Order: {question.order}</span>
                          {question.placeholder && (
                            <span>Placeholder: {question.placeholder}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleView(question)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>

                        {canManage && (
                          <div 
                            ref={el => dropdownRefs.current[question.id] = el}
                            className="relative"
                          >
                            <button
                              onClick={(e) => handleActionClick(question.id, e)}
                              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                              title="Actions"
                            >
                              <EllipsisVerticalIcon className="w-5 h-5" />
                            </button>
                            
                            {actionDropdown === question.id && (
                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-48">
                                <button
                                  onClick={() => handleEdit(question)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                  Edit Question
                                </button>
                                <button
                                  onClick={() => handleDelete(question.id)}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  Delete Question
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {filteredQuestions.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} questions
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex gap-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => 
                        page === 1 || 
                        page === totalPages || 
                        Math.abs(page - currentPage) <= 1
                      )
                      .map((page, index, array) => {
                        const showEllipsis = index < array.length - 1 && array[index + 1] - page > 1;
                        return (
                          <React.Fragment key={page}>
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-1 text-sm border rounded transition-colors ${
                                currentPage === page
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                            {showEllipsis && (
                              <span className="px-2 text-gray-500">...</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Question Details Modal - ALWAYS AVAILABLE */}
      <QuestionDetailsModal
        question={viewingQuestion}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      />
    </div>
  );
};

export default QuestionsList;